import { differenceInYears, format, isMatch, parse } from 'date-fns';
import { ADDRESS_NOT_SHARED_VALUE } from 'platform/shared/health-apps';
import get from 'platform/utilities/data/get';

const toUtcMidnight = d =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

const parseSupportedDate = dateStr => {
  if (typeof dateStr !== 'string') return null;

  const formats = ['yyyy-MM-dd', 'MM-dd-yyyy'];
  const matchedFormat = formats.find(dateFormat => {
    if (!isMatch(dateStr, dateFormat)) return false;

    const parsedDate = parse(dateStr, dateFormat, new Date());
    return format(parsedDate, dateFormat) === dateStr;
  });

  return matchedFormat ? parse(dateStr, matchedFormat, new Date()) : null;
};

/**
 * Generates a deterministic, non-cryptographic hash of a string.
 *
 * Used to create a stable identifier for linking related records without
 * duplicating the original SSN. The resulting hash is intended only as an
 * internal identifier and should not be considered secure.
 *
 * @param {string | null | undefined} val The value to hash.
 * @returns {string} A hexadecimal hash of the input string.
 */
export const toHash = val => {
  const str = val ?? '';
  let hash = 0;
  Object.keys(str).forEach(i => {
    const chr = str.charCodeAt(i);
    hash = hash * 32 - hash + chr;
    hash = Math.floor(hash);
  });
  return hash.toString(16);
};

// Generic accessors
export const getApplicant = (formData, index) => formData?.applicants?.[index];

export const applicantValue =
  (path, fallback = undefined) =>
  (formData, index) =>
    get(path, getApplicant(formData, index)) ?? fallback;

// Generic applicant predicate helpers
export const applicantFieldEquals =
  (path, expectedValue) => (formData, index) =>
    applicantValue(path)(formData, index) === expectedValue;

export const applicantFieldIn = (path, allowedValues) => (formData, index) =>
  allowedValues.includes(applicantValue(path)(formData, index));

export const applicantBoolean = path => (formData, index) =>
  Boolean(applicantValue(path)(formData, index));

// Named applicant field accessors
export const applicantDob = applicantValue('applicantDob');
export const applicantRelationship = applicantValue(
  'applicantRelationshipToSponsor',
);
export const applicantRelationshipOrigin = applicantValue(
  'applicantRelationshipOrigin',
);
export const applicantDependentStatus = applicantValue(
  'applicantDependentStatus',
);
export const applicantSharedAddress = applicantValue(
  'view:applicantSharedAddress',
);
export const applicantRemarried = applicantValue('applicantRemarried', false);

// Relationship / status predicates
export const applicantRelationshipIs = relationship =>
  applicantFieldEquals('applicantRelationshipToSponsor', relationship);

export const applicantOriginIs = origin =>
  applicantFieldEquals('applicantRelationshipOrigin', origin);

export const applicantDependentStatusIn = statuses =>
  applicantFieldIn('applicantDependentStatus', statuses);

// Shared applicant predicates
export const applicantIsChild = applicantRelationshipIs('child');
export const applicantIsSpouse = applicantRelationshipIs('spouse');

// Address-sharing predicates
export const applicantHasNoSharedAddressSelection = (formData, index) => {
  const sharedWith = applicantSharedAddress(formData, index);
  return !sharedWith || sharedWith === ADDRESS_NOT_SHARED_VALUE;
};

// Age helpers
export const getAgeInYears = (dateStr, asOf = new Date()) => {
  const parsedDate = parseSupportedDate(dateStr);
  if (!parsedDate) return NaN;

  const dobUtc = toUtcMidnight(parsedDate);
  const asOfUtc = toUtcMidnight(asOf);
  return differenceInYears(asOfUtc, dobUtc);
};

export const applicantIsCollegeAge = (formData, index) => {
  const age = getAgeInYears(applicantDob(formData, index));
  return age >= 18 && age <= 23;
};

/**
 * Birth certificate rules
 *
 * Require a birth certificate when:
 * - applicant is a child, AND
 *   - origin is not blood, OR
 *   - origin is blood and applicant is a newborn (<= 1 year old)
 */
export const requireBirthCertificate = (formData, index) => {
  if (!getApplicant(formData, index) || !applicantIsChild(formData, index)) {
    return false;
  }
  if (!applicantOriginIs('blood')(formData, index)) return true;
  const dob = applicantDob(formData, index);
  const age = getAgeInYears(dob);
  return age >= 0 && age <= 1;
};

// Applicant-specific state predicates
export const applicantHasRemarried = applicantBoolean('applicantRemarried');

export const hasMultipleApplicants = formData => {
  return (formData?.applicants ?? []).length > 1;
};
